/**
 * Retry ringan untuk load data PENTING di PWA Absensi (status hari ini,
 * daftar workspace).
 *
 * Latar (tiket Silvi/Voiz 4 Agu 2026): saat D1 overload, GET /attendance/today
 * gagal → halaman diam-diam menampilkan form clock-in (seolah belum absen) dan
 * tombol clock-out hilang; kalau daftar workspace yang gagal, halaman macet di
 * "Memuat…" selamanya. Retry ini + state error eksplisit di HomePage mencegah
 * dua gejala itu.
 */

type HttpishError = {
    code?: string;
    name?: string;
    isAxiosError?: boolean;
    response?: { status?: number; headers?: unknown };
};

/** Retry-After lebih lama dari ini → jangan tunggu di foreground. */
const MAX_RETRY_AFTER_MS = 8000;
export const DEFAULT_RETRY_DELAYS_MS = [1500, 4000];

/**
 * Error sementara yang layak dicoba ulang otomatis:
 *   - jaringan putus / timeout (axios tanpa response)
 *   - 502 / 503 (db_busy) / 504
 *   - 401 yang SAMPAI ke pemanggil: interceptor api.ts hanya meneruskan 401 bila
 *     refresh token gagal SEMENTARA (server sibuk) — refresh yang benar-benar
 *     ditolak sudah diarahkan ke /login. Mengulang request memicu refresh lagi.
 * Selain itu (4xx lain, 500, dibatalkan) → tidak di-retry.
 */
export function isRetryableLoadError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const e = err as HttpishError;
    if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError') return false;
    const status = e.response?.status;
    if (typeof status !== 'number') {
        return e.isAxiosError === true || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT';
    }
    return status === 401 || status === 502 || status === 503 || status === 504;
}

/** Retry-After (detik) dari response error → ms; null kalau tidak ada / tak valid. */
export function retryAfterMs(err: unknown): number | null {
    const headers = (err as HttpishError | null)?.response?.headers as
        | { get?: (n: string) => unknown; [k: string]: unknown }
        | undefined;
    if (!headers || typeof headers !== 'object') return null;
    let raw: unknown;
    if (typeof headers.get === 'function') {
        try { raw = headers.get('retry-after'); } catch { raw = undefined; }
    }
    if (raw == null) raw = headers['retry-after'] ?? headers['Retry-After'];
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    return Math.round(parseFloat(s) * 1000);
}

export interface RetryOptions {
    /** Jeda sebelum tiap percobaan ulang; panjang array = jumlah retry maksimum. */
    delaysMs?: number[];
    shouldRetry?: (err: unknown) => boolean;
    sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Jalankan `fn`; bila gagal karena error sementara, ulangi dengan jeda bertahap. */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
    const delays = opts.delaysMs ?? DEFAULT_RETRY_DELAYS_MS;
    const shouldRetry = opts.shouldRetry ?? isRetryableLoadError;
    const sleep = opts.sleep ?? defaultSleep;
    for (let attempt = 0; ; attempt++) {
        try {
            return await fn();
        } catch (err) {
            if (attempt >= delays.length || !shouldRetry(err)) throw err;
            const hinted = retryAfterMs(err);
            // Retry-After terlalu lama → jangan gantung UI; biar user tekan "Coba lagi".
            if (hinted != null && hinted > MAX_RETRY_AFTER_MS) throw err;
            await sleep(Math.max(delays[attempt], hinted ?? 0));
        }
    }
}
